import React, { useState } from 'react';
import { Formik } from 'formik';

import { useStyles } from '~/assets/styles';
import CardDialog from '~/components/CardDialog';
import Button from '~/components/core/Atomic/Buttons/Button';
import CancelButton from '~/components/core/Buttons/CancelButton';
import Text from '~/components/core/TextComponents/Text';
import useOrganization from '~/components/OrganizationContext';
import { PAYMENTS_CONFIG_FIELDS } from '~/components/SystemConfiguration/Tabs/PaymentsConfiguration/PaymentConfigTab/constants';
import type { PaymentsConfiguration } from '~/components/SystemConfiguration/Tabs/PaymentsConfiguration/types';
import { CoveragesMultiselectFormik } from '~/components/TPA/Coverages/CoveragesMultiselectFormik';
import { LobMultiSelect } from '~/components/TPA/LOB/LobMultiselect';

const COVERAGES_FIELD_ID =
  PAYMENTS_CONFIG_FIELDS.CONTACT_AU_TFN_MANDATORY_COVERAGES as 'contact_au_tfn_mandatory_coverages';

// LobMultiSelect is an untyped .jsx component; type the props we use.
const TypedLobMultiSelect = LobMultiSelect as unknown as React.FC<{
  subOrganizationIds: number[];
  onChange?: (lobs: string[]) => void;
  disabled?: boolean;
  label?: string;
}>;
// Stable reference so CoveragesMultiselectFormik's effects don't see a new array each render.
const EMPTY_SUB_ORG_IDS: number[] = [];

interface SubOrganization {
  id: number;
}

interface TfnMandatoryCoveragesValues {
  contact_au_tfn_mandatory_coverages: string[];
}

interface TfnMandatoryCoveragesDialogProps {
  paymentsConfiguration: PaymentsConfiguration;
  disabled?: boolean;
  onCancel: () => void;
  onSubmit: (values: TfnMandatoryCoveragesValues) => Promise<void>;
}

const TfnMandatoryCoveragesDialog: React.FC<TfnMandatoryCoveragesDialogProps> = ({
  paymentsConfiguration,
  disabled = false,
  onCancel,
  onSubmit,
}) => {
  const { classes } = useStyles();
  const { subOrganizations, supportedClaimTypes, subOrganizationEnabled } = useOrganization() as {
    subOrganizations: SubOrganization[];
    supportedClaimTypes: string[];
    subOrganizationEnabled: boolean;
  };
  // Browsing filter only — the saved setting is a flat list of coverage keys with no LOB dimension.
  const [lobsFilter, setLobsFilter] = useState<string[]>([]);
  // Coverages are loaded per LOB and (for sub-org-enabled orgs) require the sub-org ids;
  // passing [] would disable the multiselect. Use all sub-orgs / all org claim types.
  const subOrganizationIds = subOrganizationEnabled ? subOrganizations.map((subOrg) => subOrg.id) : EMPTY_SUB_ORG_IDS;

  return (
    <Formik<TfnMandatoryCoveragesValues>
      initialValues={{
        [COVERAGES_FIELD_ID]: paymentsConfiguration?.[COVERAGES_FIELD_ID] ?? [],
      }}
      enableReinitialize
      onSubmit={async (values, formikProps) => {
        try {
          await onSubmit(values);
        } catch (error) {
          formikProps.setSubmitting(false);
        }
      }}
    >
      {({ handleSubmit }) => (
        <CardDialog title="Mandatory TFN coverages" isDialog onClose={onCancel} preventClose={disabled} maxWidth="sm">
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
            Select the coverages for which the payee contact&apos;s TFN must be verified before making a payment. Leave
            empty to require no specific coverage.
          </Text>
          <div>
            <TypedLobMultiSelect
              label="Filter by line of business"
              subOrganizationIds={subOrganizationIds}
              onChange={setLobsFilter}
              disabled={disabled}
            />
          </div>
          <div>
            <CoveragesMultiselectFormik
              label="Coverages"
              lobs={lobsFilter.length ? lobsFilter : supportedClaimTypes || []}
              subOrganizationIds={subOrganizationIds}
              coveragesFieldId={COVERAGES_FIELD_ID}
              disableGeneral
              disabled={disabled}
              preserveSelectionOnLobsChange
            />
          </div>
          <div className={classes.buttonsContainer}>
            <CancelButton disabled={disabled} onClick={onCancel} />
            <Button variant="contained" color="primary" disabled={disabled} onClick={() => handleSubmit()}>
              Save
            </Button>
          </div>
        </CardDialog>
      )}
    </Formik>
  );
};

export default TfnMandatoryCoveragesDialog;
